import { useEffect, useState } from "react";
import { Settings, Save, Accessibility, FileText, Hammer } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import AdminLegalDocumentsTab from "@/pages/admin/AdminLegalDocumentsTab";
import AdminConstructionTab from "@/pages/admin/AdminConstructionTab";

export default function AdminSettings() {
    const [tab, setTab] = useState("referent");
    const [referent, setReferent] = useState(null);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (tab !== "referent") return;
        api.get("/admin/settings/referent-handicap")
            .then((r) => setReferent(r.data))
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    }, [tab]);

    const set = (k, v) => setReferent((r) => ({ ...r, [k]: v }));

    const setAdaptation = (idx, v) => {
        setReferent((r) => ({
            ...r,
            adaptations: r.adaptations.map((a, i) => (i === idx ? v : a)),
        }));
    };
    const addAdaptation = () => setReferent((r) => ({ ...r, adaptations: [...(r.adaptations || []), ""] }));
    const removeAdaptation = (idx) => setReferent((r) => ({
        ...r,
        adaptations: r.adaptations.filter((_, i) => i !== idx),
    }));

    const save = async () => {
        if (!referent.name || !referent.email || !referent.message || referent.message.length < 20) {
            toast.error("Nom, email et message (≥20 chars) sont obligatoires.");
            return;
        }
        setSaving(true);
        try {
            await api.put("/admin/settings/referent-handicap", {
                ...referent,
                adaptations: (referent.adaptations || []).filter(Boolean),
            });
            toast.success("Paramètres enregistrés");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    if (tab === "referent" && (loading || !referent)) {
        return <div className="mx-auto max-w-4xl px-6 py-16 text-center text-brand-smoke">Chargement…</div>;
    }

    return (
        <div className="mx-auto max-w-5xl px-6 py-10" data-testid="page-admin-settings">
            <div className="flex items-start justify-between gap-6">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-orange">Paramètres</div>
                    <h1 className="mt-2 font-display text-5xl font-normal tracking-tightest">Paramètres plateforme</h1>
                </div>
                <div className="hidden h-16 w-16 items-center justify-center rounded-2xl bg-brand-orange/10 text-brand-orange md:flex">
                    <Settings className="h-7 w-7" />
                </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-2 border-b border-brand-line" data-testid="settings-tabs">
                <TabBtn active={tab === "referent"} onClick={() => setTab("referent")} icon={Accessibility} label="Référent handicap" testId="settings-tab-referent" />
                <TabBtn active={tab === "legal"} onClick={() => setTab("legal")} icon={FileText} label="Documents légaux" testId="settings-tab-legal" />
                <TabBtn active={tab === "construction"} onClick={() => setTab("construction")} icon={Hammer} label="Mode public" testId="settings-tab-construction" />
            </div>

            {tab === "construction" ? (
                <div className="mt-8"><AdminConstructionTab /></div>
            ) : tab === "legal" ? (
                <div className="mt-8"><AdminLegalDocumentsTab /></div>
            ) : (
            <div className="mt-8 rounded-2xl border border-brand-line bg-white p-8 shadow-premium" data-testid="settings-referent-handicap">
                <div className="flex items-center gap-3">
                    <Accessibility className="h-5 w-5 text-brand-orange" />
                    <h2 className="font-display text-2xl">Référent handicap & accessibilité</h2>
                </div>
                <p className="mt-2 text-sm text-brand-smoke">
                    Ces informations sont publiées sur la page <code>/accessibilite</code> et utilisées pour identifier la personne en charge.
                </p>

                <div className="mt-8 grid gap-5 md:grid-cols-2">
                    <div>
                        <label className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">Nom complet *</label>
                        <input
                            value={referent.name || ""}
                            onChange={(e) => set("name", e.target.value)}
                            data-testid="referent-input-name"
                            className="mt-2 w-full rounded-xl border border-brand-line px-4 py-2.5 outline-none focus:border-brand-orange"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">Fonction</label>
                        <input
                            value={referent.role || ""}
                            onChange={(e) => set("role", e.target.value)}
                            className="mt-2 w-full rounded-xl border border-brand-line px-4 py-2.5 outline-none focus:border-brand-orange"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">Email *</label>
                        <input
                            type="email"
                            value={referent.email || ""}
                            onChange={(e) => set("email", e.target.value)}
                            data-testid="referent-input-email"
                            className="mt-2 w-full rounded-xl border border-brand-line px-4 py-2.5 outline-none focus:border-brand-orange"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">Téléphone</label>
                        <input
                            value={referent.phone || ""}
                            onChange={(e) => set("phone", e.target.value)}
                            className="mt-2 w-full rounded-xl border border-brand-line px-4 py-2.5 outline-none focus:border-brand-orange"
                        />
                    </div>
                </div>
                <div className="mt-5">
                    <label className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">Photo (URL)</label>
                    <input
                        value={referent.photo_url || ""}
                        onChange={(e) => set("photo_url", e.target.value)}
                        placeholder="https://…/photo.jpg"
                        className="mt-2 w-full rounded-xl border border-brand-line px-4 py-2.5 outline-none focus:border-brand-orange"
                    />
                    {referent.photo_url && (
                        <img src={referent.photo_url} alt="preview" className="mt-3 h-24 w-24 rounded-xl object-cover" />
                    )}
                </div>
                <div className="mt-5">
                    <label className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">
                        Message public * <span className="text-brand-smoke/60 normal-case">({(referent.message || "").length}/2000)</span>
                    </label>
                    <textarea
                        rows={6}
                        value={referent.message || ""}
                        onChange={(e) => set("message", e.target.value)}
                        data-testid="referent-input-message"
                        className="mt-2 w-full resize-none rounded-xl border border-brand-line px-4 py-3 outline-none focus:border-brand-orange"
                    />
                </div>

                <div className="mt-5">
                    <div className="flex items-center justify-between">
                        <label className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">Adaptations possibles</label>
                        <button
                            type="button"
                            onClick={addAdaptation}
                            className="text-xs text-brand-orange hover:underline"
                        >+ Ajouter</button>
                    </div>
                    <div className="mt-3 space-y-2">
                        {(referent.adaptations || []).map((a, i) => (
                            <div key={i} className="flex items-center gap-2">
                                <input
                                    value={a}
                                    onChange={(e) => setAdaptation(i, e.target.value)}
                                    className="flex-1 rounded-xl border border-brand-line px-4 py-2 outline-none focus:border-brand-orange"
                                />
                                <button
                                    type="button"
                                    onClick={() => removeAdaptation(i)}
                                    className="text-xs text-brand-smoke hover:text-red-600"
                                >Supprimer</button>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="mt-8 flex items-center justify-end">
                    <button
                        onClick={save}
                        disabled={saving}
                        data-testid="referent-save"
                        className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-3 text-sm font-semibold text-white hover:bg-[#d9772c] disabled:opacity-60"
                    >
                        <Save className="h-3.5 w-3.5" />
                        {saving ? "Enregistrement…" : "Enregistrer"}
                    </button>
                </div>
            </div>
            )}
        </div>
    );
}

function TabBtn({ active, onClick, icon: Icon, label, testId }) {
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold transition border-b-2 -mb-px ${
                active ? "border-brand-orange text-brand-orange" : "border-transparent text-brand-smoke hover:text-brand-ink"
            }`}
        >
            <Icon className="h-3.5 w-3.5" /> {label}
        </button>
    );
}
